import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { useRaptorPlatforms } from '../../../api/rs3-public/raptorPlatform';
import { raptorPlatformIdSelector } from '../../../redux/raptorPlatform/selectors';
import { setRaptorPlatformId } from '../../../redux/raptorPlatform/actions';
import TokenService from '../../../services/token-service';
import LoadingSpinner from '../../ui/LoadingSpinner';

const RaptorPlatformSelector: React.FC = () => {
  const dispatch = useDispatch();
  const selectedPlatformId = useSelector(raptorPlatformIdSelector);

  const { user_client_id } = TokenService.getUser()!;

  const {
    data: platforms,
    isLoading,
    error,
  } = useRaptorPlatforms(
    user_client_id,
    false, // Don't include inactive raptorplatform
    {
      enabled: !!user_client_id,
    },
  );

  const handlePlatformChange = (platformId: string | null) => {
    dispatch(setRaptorPlatformId(platformId || undefined));
  };

  // Don't render if loading, error, or no raptorplatform
  if (isLoading) {
    return (
      <div className="min-w-[180px]">
        <div className="flex items-center justify-center rounded border border-gray-200 bg-gray-50 px-3 py-2">
          <LoadingSpinner className="h-4 w-4" />
          <span className="ml-2 text-sm text-gray-600">
            Loading platforms...
          </span>
        </div>
      </div>
    );
  }

  if (error || !platforms || platforms.length === 0) {
    return null;
  }

  const sortedPlatforms = [...platforms].sort((a, b) =>
    a.raptor_platform_name.localeCompare(b.raptor_platform_name),
  );

  // Build options with "All Platforms" first, then all platforms in alphabetical order
  const allOptions = [
    { id: null, name: 'All Platforms', isNoPlatform: true },
    ...sortedPlatforms.map((p) => ({
      id: p.raptor_platform_id,
      name: p.raptor_platform_name,
      isNoPlatform: false,
    })),
  ];

  const selectedPlatform = platforms.find(
    (p) => p.raptor_platform_id === selectedPlatformId,
  );

  return (
    <div className="min-w-[180px]">
      <Listbox
        value={selectedPlatformId || null}
        onChange={handlePlatformChange}
      >
        <div className="relative">
          <ListboxButton className="relative w-full cursor-pointer rounded border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm font-medium text-mainblue shadow-sm transition-all duration-200 hover:border-mainblue hover:shadow-md focus:border-mainblue focus:outline-none focus:ring-1 focus:ring-mainblue focus:ring-opacity-20">
            <div className="flex items-center">
              <div className="mr-2 flex-shrink-0">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
              </div>
              <span className="block truncate">
                {selectedPlatform
                  ? selectedPlatform.raptor_platform_name
                  : 'All Platforms'}
              </span>
            </div>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg
                className="h-4 w-4 text-mainblue"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04L10 14.148l2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </ListboxButton>

          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions className="absolute z-10 mt-2 w-full rounded-lg border border-gray-100 bg-white py-2 text-base shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {allOptions.map((option) => (
                <ListboxOption
                  key={option.id || 'no-platform'}
                  value={option.id}
                  className={({ focus, selected }) =>
                    `group relative cursor-pointer select-none px-4 py-3 text-gray-900 transition-colors duration-150 ${
                      focus ? 'bg-blue-100 text-blue-900' : ''
                    } ${option.isNoPlatform ? 'border-b border-gray-200' : ''}`
                  }
                >
                  {({ selected }) => (
                    <>
                      <div className="flex items-center">
                        <div className="mr-3 flex-shrink-0">
                          <div
                            className={`h-2 w-2 rounded-full ${
                              selected ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          ></div>
                        </div>
                        <span className="block truncate font-normal">
                          {option.name}
                        </span>
                      </div>
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

export default React.memo(RaptorPlatformSelector);
